import type { JSONSchema7, JSONSchema7TypeName } from 'json-schema';

type PropertyType = JSONSchema7TypeName[] | JSONSchema7TypeName | undefined;

function parseSearchParam(value: string, propertyType?: PropertyType): unknown {
  let parsedValue: unknown;
  try {
    parsedValue = JSON.parse(value);
  } catch {
    parsedValue = value;
  }

  if (!propertyType) {
    return parsedValue;
  }

  switch (propertyType) {
    case 'string': {
      // In many cases, we have declared the type of property as string, but have passed object/array value to it
      if (typeof parsedValue === 'object') return parsedValue;
      return String(parsedValue);
    }
    case 'boolean': {
      if (typeof parsedValue === 'boolean') return parsedValue;
      return parsedValue === 'true';
    }
    default:
      return parsedValue;
  }
}

/**
 * extracts and parses page inputs from URL search params based on schema
 *
 * when schema is present, only handles the keys in schema.properties,
 * and simply applies guessTypeFromValue for each key if present in search params.
 * otherwise, parses all query params with smart type detection.
 */
export function parseSearchParamsFromSchema(
  schema: JSONSchema7 | undefined,
  searchParams: URLSearchParams,
): Record<string, unknown> {
  const pageInputs: Record<string, unknown> = {};

  if (schema?.properties && typeof schema.properties === 'object') {
    for (const key of Object.keys(schema.properties)) {
      const value = searchParams.get(key);
      const propertySchema = schema.properties[key];
      const propertyType =
        typeof propertySchema === 'object' && 'type' in propertySchema
          ? propertySchema.type
          : undefined;

      if (value !== null) {
        pageInputs[key] = parseSearchParam(value, propertyType);
      }
    }
    return pageInputs;
  }

  for (const [key, value] of searchParams.entries()) {
    pageInputs[key] = parseSearchParam(value);
  }
  return pageInputs;
}
